import { useCallback } from 'react';

import { CloseIcon } from '@braskai/icons';
import { Button, Modal, Typography } from '@braskai/ui-kit';
import { useNavigate } from 'react-router-dom';

import { Stack } from '@mui/material';
import { Box } from '@mui/system';

import { useCurrentUser } from '@/entities/user/currentUser';

import { FaceSadIcon } from '@/shared/assets/icons/FaceSadIcon';
import { FeaturedDowngradeIcon } from '@/shared/assets/icons/FeaturedDowngradeIcon';
import { PATHS } from '@/shared/constants/routes/paths';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PaymentInterval, Price } from '@/shared/types/Billing';

import { usePlanChangeConfirmation } from '../../../model/usePlanChangeConfirmation';
import { useDowngradeConfirmationModalStore } from '../../model/useDowngradeConfirmationModalStore';
import {
  SActions,
  SCloseButton,
  SContent,
  SHeader,
  SHeaderTop,
  SModalContainer,
  SNewPlanSection,
  SPlanSection,
} from './styled';

const formatDate = (date: Date | string) => {
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const getDowngradeEffectiveDate = (targetPrice: Price) => {
  const currentDate = new Date();
  const isYearlyPlan = targetPrice.recurring?.interval === PaymentInterval.Year;

  if (isYearlyPlan) {
    currentDate.setFullYear(currentDate.getFullYear() + 1);
  } else {
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return formatDate(currentDate);
};

export const DowngradeConfirmationModal = () => {
  const isOpen = useDowngradeConfirmationModalStore((state) => state.isOpen);
  const close = useDowngradeConfirmationModalStore((state) => state.close);
  const currentPlan = useDowngradeConfirmationModalStore((state) => state.currentPlan);
  const targetPlan = useDowngradeConfirmationModalStore((state) => state.targetPlan);
  const targetPrice = useDowngradeConfirmationModalStore((state) => state.targetPrice);
  const featuresToLose = useDowngradeConfirmationModalStore((state) => state.featuresToLose);

  const { data: user } = useCurrentUser();
  const navigate = useNavigate();
  const handleClosedAfterProcessing = useCallback(() => navigate(PATHS.homepage), [navigate]);

  const openPlanChangeConfirmation = usePlanChangeConfirmation({
    onClosedAfterProcessing: handleClosedAfterProcessing,
  });

  const handleDowngrade = () => {
    if (!targetPrice?.id || !targetPlan?.id) return;

    MixpanelEvents.scheduleDowngradeClick({
      currentPlan: currentPlan?.name ?? '',
      newPlan: targetPlan.name,
    });
    close();

    openPlanChangeConfirmation({
      confirmButtonText: 'Schedule downgrade',
      errorMessage: 'Failed to downgrade plan:',
      planId: targetPlan.id,
      priceId: targetPrice.id,
      successDescriptions: {
        noPayment: 'Your downgrade has been scheduled',
        paid: 'Your plan change has been processed',
      },
      onConfirmAnalytics: () => MixpanelEvents.scheduleDowngradeConfirmClick({
        currentPlan: currentPlan?.name ?? '',
        newPlan: targetPlan.name,
      }),
    });
  };

  const handleCancel = () => {
    close();
  };

  if (!currentPlan || !targetPlan || !targetPrice) {
    return null;
  }

  const currentIntervalLabel = user?.subscription?.plan.interval === PaymentInterval.Year ? 'yearly' : 'monthly';
  const targetIntervalLabel = targetPrice.recurring?.interval === PaymentInterval.Year ? 'yearly' : 'monthly';

  return (
    <Modal open={isOpen} onClose={handleCancel}>
      <SModalContainer>
        {/* Header */}
        <SHeader>
          <SHeaderTop>
            <FeaturedDowngradeIcon color='error' height={56} width={56} />
            <SCloseButton data-testid="downgrade-confirmation-modal-cancel" onClick={handleCancel}>
              <CloseIcon height={24} width={24} />
            </SCloseButton>
          </SHeaderTop>
        </SHeader>

        {/* Content */}
        <SContent>
          {/* Current Plan */}
          <SPlanSection>
            <Typography color="grey.900" variant="body-1-md">
              Current plan
            </Typography>
            <Typography color="grey.700" variant="body-2">
              You're currently on {currentIntervalLabel} {currentPlan.name}, active until{' '}
              {user?.subscription?.current_period_end && formatDate(user.subscription.current_period_end)}.
            </Typography>
          </SPlanSection>

          {/* New Plan */}
          <SNewPlanSection>
            <SPlanSection>
              <Typography color="error.700" variant="body-1-md">
                New plan
              </Typography>
              <Typography color="grey.700" variant="body-2">
                You're switching to {targetIntervalLabel} {targetPlan.name}. The change will take effect at the end of your
                current billing period.
              </Typography>
            </SPlanSection>

            {/* Features You Lose */}
            {featuresToLose && featuresToLose.length > 0 && (
              <Stack alignItems="flex-start" border="1px solid" borderColor="grey.200" borderRadius={12} direction="row" gap="10px" p={16} sx={{ backgroundColor: 'grey.50' }}>
                <Box>
                  <FaceSadIcon height={20} width={20} />
                </Box>
                <Stack gap="2px">
                  <Typography color="grey.900" variant="body-2-md">
                    Features you lose
                  </Typography>
                  <Typography color="grey.700" variant="body-2">
                    {featuresToLose.join(', ')}
                  </Typography>
                </Stack>
              </Stack>
            )}
          </SNewPlanSection>
        </SContent>

        {/* Actions */}
        <SActions>
          <Button data-testid="downgrade-confirmation-modal-schedule-downgrade" size="lg" variant="error" fullWidth onClick={handleDowngrade}>
            Schedule downgrade
          </Button>
          <Typography color="grey.500" sx={{ textAlign: 'center' }} variant="caption-1">
            Your current plan remains active until {getDowngradeEffectiveDate(targetPrice)}.
          </Typography>
        </SActions>
      </SModalContainer>
    </Modal>
  );
};
